import { faCheck } from '@fortawesome/pro-light-svg-icons';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, InputField } from '@pickleballinc/react-ui';
import { forwardRef } from 'react';

import { MobileFilterRow } from '@/modules/rankings/MobileFilter/MobileFilterRow';
import { PlayerFiltersInterface } from '@/types/ranking.types';

interface StateMobileProps {
  state: {
    id: number;
    title: string;
  };
  stateDropdownOpen: boolean;
  toggleStateDropdown: () => void;
  stateDropdownRef: React.RefObject<HTMLDivElement | null>;
  stateSearchInput: string;
  statesList: Array<{ id: number; title: string }>;
  filters: PlayerFiltersInterface;
  setFilters: React.Dispatch<React.SetStateAction<PlayerFiltersInterface>>;
  handleStateSearchInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleClickCancel: () => void;
  handleClickNext: () => void;
}

const StateMobile = forwardRef<HTMLDivElement, StateMobileProps>(
  (
    {
      state,
      stateDropdownOpen,
      toggleStateDropdown,
      stateDropdownRef,
      stateSearchInput,
      statesList,
      filters,
      setFilters,
      handleStateSearchInputChange,
      handleClickCancel,
      handleClickNext,
    },
    ref
  ) => {
    return (
      <div ref={ref}>
        {!stateDropdownOpen ? (
          <MobileFilterRow
            label='State'
            value={state.title}
            onClick={toggleStateDropdown}
            testId='rankings-state-button'
          />
        ) : (
          <div
            className='mx-auto w-full max-w-md rounded-lg border border-gray-200 bg-white p-4 shadow-md'
            ref={stateDropdownRef}
          >
            <h2 className='mb-4 text-lg font-semibold' onClick={toggleStateDropdown}>
              State
            </h2>

            <div className='relative w-full p-3'>
              <InputField
                autoFocus
                placeholder='Search States'
                value={stateSearchInput}
                onChange={handleStateSearchInputChange}
                PrefixIcon={() => <FontAwesomeIcon icon={faMagnifyingGlass} />}
                className='w-full rounded-md border p-1 text-xl'
                data-testid='rankings-state-search'
              />
            </div>

            <div className='mb-4 max-h-[45vh] space-y-2 overflow-y-auto rounded-md border border-gray-200'>
              {statesList
                .filter((option) =>
                  option.title.toLowerCase().includes(stateSearchInput.toLowerCase())
                )
                .map((option) => {
                  return (
                    <div
                      key={String(option.id)}
                      onClick={() => setFilters({ ...filters, state: String(option.id) })}
                      className={`m-1 flex cursor-pointer justify-between rounded-lg p-3 text-gray-900 ${
                        filters.state === String(option.id) ? 'bg-gray-50' : 'bg-white'
                      }`}
                      data-testid={`rankings-state-option-${option.title}`}
                    >
                      {option.title}
                      {filters.state === String(option.id) && (
                        <span className='ml-2 text-purple-600'>
                          <FontAwesomeIcon icon={faCheck} />
                        </span>
                      )}
                    </div>
                  );
                })}
            </div>

            <div className='flex justify-between gap-2'>
              <Button
                className='flex w-[150px] items-center justify-center rounded-lg border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100'
                onClick={handleClickCancel}
                variant='secondary'
                data-testid='rankings-state-button-cancel'
              >
                Cancel
              </Button>
              <Button
                className='flex w-[150px] items-center justify-center rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700'
                onClick={handleClickNext}
                variant='primary'
                data-testid='rankings-state-button-next'
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
);

// Add display name for better debugging
StateMobile.displayName = 'StateMobile';

export default StateMobile;
